import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Users, Layers, Palette, MapPin } from "lucide-react";
import AllocateTeamModal from "@/components/design/AllocateTeamModal";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function DesignQueuePage() {
  const { principal, isAdmin, roleName, roleType } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alloc, setAlloc] = useState({ open: false, project: null });
  const nav = useNavigate();

  const canAllocate = isAdmin || roleType === "director" || roleName === "Design Head";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/design/projects");
      setProjects(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const unassigned = projects.filter(p => !p.interior_designer_id);
  const assigned = projects.filter(p => p.interior_designer_id);

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Design · Head</p>
        <h1 className="font-display text-3xl text-white mt-1">Allocation Queue</h1>
        <p className="text-xs text-gray-500 font-mono mt-1">Auto-populated when a Sales lead is closed as Won.</p>
      </div>

      <div className="px-10 py-8">
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="iz-card p-5">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500">Total Projects</p>
            <p className="font-display text-3xl mt-1 text-white">{projects.length}</p>
          </div>
          <div className="iz-card p-5 border-[#d4af37]/40">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#d4af37]">Unassigned</p>
            <p className="font-display text-3xl mt-1 text-[#d4af37]">{unassigned.length}</p>
          </div>
          <div className="iz-card p-5">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500">Assigned</p>
            <p className="font-display text-3xl mt-1 text-white">{assigned.length}</p>
          </div>
        </div>

        <div className="mb-8">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">Incoming · Unassigned</p>
          {loading ? (
            <p className="text-xs text-gray-500 text-center py-6">Loading…</p>
          ) : unassigned.length === 0 ? (
            <div className="iz-card p-8 text-center">
              <p className="text-sm text-gray-500">No unassigned projects.</p>
              <p className="text-xs text-gray-600 mt-2">New Sales "Won" leads will land here automatically.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {unassigned.map(p => (
                <div key={p.id} className="iz-card p-4 flex items-center justify-between hover:border-[#d4af37]/40 transition-colors" data-testid={`queue-row-${p.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <p className="text-white font-medium">{p.client_name}{p.city ? ` · ${p.city}` : ""}</p>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] bg-[#d4af37]/10 border border-[#d4af37]/30 rounded px-2 py-0.5">Awaiting allocation</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">{p.project_name}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="text-[#d4af37] font-mono">₹{(p.project_amount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => nav(`/design/projects/${p.id}`)}
                      className="iz-btn-secondary flex items-center gap-2 text-xs"
                      data-testid={`view-files-${p.id}`}
                      title="View sales attachments before assigning the team"
                    >
                      View Sales Files
                    </button>
                    {canAllocate && (
                      <button onClick={() => setAlloc({ open: true, project: p })} className="iz-btn-primary flex items-center gap-2" data-testid={`allocate-${p.id}`}>
                        <Users className="w-4 h-4"/> Assign Team
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-3">Recently allocated</p>
          {assigned.length === 0 ? (
            <p className="text-xs text-gray-600 text-center py-6">No allocated projects yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {assigned.slice(0, 6).map(p => (
                <button
                  key={p.id}
                  onClick={() => nav(`/design/projects/${p.id}`)}
                  className="iz-card p-4 text-left hover:border-[#d4af37]/40 transition-colors"
                  data-testid={`assigned-${p.id}`}
                >
                  <p className="text-white text-sm">{p.client_name}{p.city ? ` · ${p.city}` : ""}</p>
                  <p className="text-[11px] text-gray-500 mt-1">{p.project_name}</p>
                  <div className="flex items-center gap-3 mt-3 text-[10px] font-mono uppercase tracking-widest text-gray-500">
                    <span className="flex items-center gap-1"><Palette className="w-3 h-3 text-[#d4af37]"/>{p.status.replace(/_/g," ")}</span>
                    <span>· {p.iteration_count} iterations</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <AllocateTeamModal
        open={alloc.open}
        project={alloc.project}
        onClose={() => setAlloc({ open: false, project: null })}
        onSaved={() => { setAlloc({ open: false, project: null }); load(); }}
      />
    </div>
  );
}
